/**
 * Every keyboard shortcut in the product, declared once.
 *
 * They used to be eight ad-hoc `useEffect`s scattered across the components
 * that happened to own the behaviour — two of them on `document` and the rest
 * on `window`, with three separate implementations of "is the user typing?"
 * and two bindings (⌘K, ⌘J) that had no such guard at all, so ⌘K inside a
 * comment box opened the command palette.
 *
 * Nothing was written down anywhere, so ⌘B and `[` were undiscoverable, and
 * the cheatsheet this table feeds could not have been kept honest by hand.
 */
export type ShortcutScope = "global" | "list"

export interface Shortcut {
  /** Stable id, used by useShortcut to look the binding up. */
  id: string
  /** Rendered in the cheatsheet, one <kbd> per entry. */
  keys: string[]
  /** A second binding for the same action, shown as "or". Two ways in is fine;
   *  two cheatsheet rows claiming to be different things is not. */
  altKeys?: string[]
  label: string
  group: "Navigate" | "Create" | "Lists" | "View"
  scope: ShortcutScope
  /**
   * A bare key that would otherwise be swallowed by whatever you are typing
   * into. Modifier combinations set this false — ⌘K should work from an input,
   * it just should not work from a rich-text editor that has claimed it.
   */
  bare: boolean
}

export const SHORTCUTS: Shortcut[] = [
  {
    id: "palette",
    keys: ["⌘", "K"],
    label: "Search, jump to, or create",
    group: "Navigate",
    scope: "global",
    bare: false,
  },
  {
    id: "create",
    keys: ["⌘", "J"],
    label: "Create a record",
    group: "Create",
    scope: "global",
    bare: false,
  },
  {
    id: "help",
    keys: ["?"],
    label: "Keyboard shortcuts",
    group: "View",
    scope: "global",
    bare: true,
  },
  {
    id: "sidebar",
    keys: ["["],
    altKeys: ["⌘", "B"],
    label: "Toggle the sidebar",
    group: "View",
    scope: "global",
    bare: true,
  },
  {
    id: "search-list",
    keys: ["/"],
    label: "Search this list",
    group: "Lists",
    scope: "list",
    bare: true,
  },
  {
    id: "row-down",
    keys: ["j"],
    label: "Move down a row",
    group: "Lists",
    scope: "list",
    bare: true,
  },
  {
    id: "row-up",
    keys: ["k"],
    label: "Move up a row",
    group: "Lists",
    scope: "list",
    bare: true,
  },
  {
    id: "row-open",
    keys: ["⏎"],
    label: "Open the focused row",
    group: "Lists",
    scope: "list",
    bare: true,
  },
  {
    id: "row-select",
    keys: ["x"],
    label: "Select the focused row",
    group: "Lists",
    scope: "list",
    bare: true,
  },
]

/**
 * Opening the cheatsheet from somewhere that is not the `?` key.
 *
 * The command palette used to dispatch a synthetic `?` keydown, so that the
 * cheatsheet stayed the single owner of that key. It did not work: closing the
 * palette is a React state update, so its dialog was still in the DOM with
 * `data-state="open"` when the event fired, and `useShortcut`'s open-dialog
 * guard — added in the same change — stood the binding down.
 *
 * A programmatic open is not a keyboard event and should not have to satisfy
 * keyboard guards. This is a plain custom event instead.
 */
export const OPEN_SHORTCUTS_EVENT = "inco:open-shortcuts"

export function openShortcutsDialog() {
  window.dispatchEvent(new CustomEvent(OPEN_SHORTCUTS_EVENT))
}

export const SHORTCUT_GROUPS = ["Navigate", "Create", "Lists", "View"] as const

export function shortcutsByGroup() {
  return SHORTCUT_GROUPS.map((group) => ({
    group,
    shortcuts: SHORTCUTS.filter((shortcut) => shortcut.group === group),
  })).filter((entry) => entry.shortcuts.length > 0)
}
